import React, { useState, useEffect, useMemo } from 'react';
import { ArrowUpDown} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MultiSelect from '../componets/MultiSelect';
import { getDadosLancamentos } from '../api';
import SideBarNavOperacao from '../componets/SideBarNavOperacao';

const CustosDetalhados = () => {
  const parseBRL = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const str = String(value).trim();
    if (!str) return 0;
    const limpo = str.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
    const numero = Number(limpo);
    return isNaN(numero) ? 0 : numero;
  };

  const [dadosLancamentos, setDadosLancamentos] = useState([]);
  const [agrupado, setAgrupado] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandidos, setExpandidos] = useState({});
  const [error, setError] = useState(null);  
  
  const [filters, setFilters] = useState({
    projeto: [],
    categoria: [],
    empresa: [],
    conta: [],
    mes: '',
    ano: ''
  });

  const [ordenacao, setOrdenacao] = useState({
    campo: null,
    direcao: 'asc'
  });

  const navigate = useNavigate();

  useEffect(() => {
    carregarLancamentos();
  }, []);

  const carregarLancamentos = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getDadosLancamentos();
      const dados = result.data
        .filter(item => item['Departamento'] === 'OPERAÇÃO')
        .map(item => {
          const dataCompleta = item['Data (completa)'] || '';
          let mes = null;
          let ano = null;
          if (dataCompleta.includes('/')) {
            const partes = dataCompleta.split('/');
            if (partes.length === 3) {
              mes = Number(partes[1]);
              ano = Number(partes[2]);
            }
          }
          return {
            empresa: item['Cliente ou Fornecedor'] || 'N/A',
            categoria: item['Categoria'] || 'N/A',
            conta: item['Conta Corrente'] || 'N/A',
            projeto: item['Projeto'] || 'N/A',
            valor: parseBRL(item['Valor'] || 0),
            departamento: item['Departamento'],
            data: dataCompleta,
            mes,
            ano
          };
        });
      setDadosLancamentos(dados);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ========= LÓGICA DE RATEIO (MANTIDA DO ORIGINAL) =========
  useEffect(() => {
    const mapa = dadosLancamentos.reduce((acc, item) => {
      if (!acc[item.projeto]) acc[item.projeto] = [];
      acc[item.projeto].push(item);
      return acc;
    }, {});

    let agrupamentoInicial = Object.entries(mapa).map(([projeto, itens]) => ({
      projeto,
      itens,
      total: itens.reduce((s, i) => s + i.valor, 0)
    }));

    const projetoRateio = agrupamentoInicial.find(p => p.projeto === 'RATEIO DE OPERAÇÕES');
    if (projetoRateio) {
      const projetosParaRatear = agrupamentoInicial.filter(p => p.projeto !== 'RATEIO DE OPERAÇÕES');
      const totalOutrosProjetos = projetosParaRatear.reduce((s, p) => s + Math.abs(p.total), 0);
      
      if (totalOutrosProjetos > 0) {
        agrupamentoInicial = agrupamentoInicial.map(grupo => {
          if (grupo.projeto === 'RATEIO DE OPERAÇÕES') return grupo;
          const proporcao = Math.abs(grupo.total) / totalOutrosProjetos;
          const valorRateio = projetoRateio.total * proporcao;
          const itemRateio = {
            empresa: 'RATEIO',
            categoria: 'Rateio de Operações',
            conta: 'Rateio Proporcional',
            projeto: grupo.projeto,
            valor: valorRateio,
            departamento: 'OPERAÇÃO',
            data: '',
            mes: null,
            ano: null,
            isRateio: true 
          };
          return {
            ...grupo,
            itens: [...grupo.itens, itemRateio],
            total: grupo.total + valorRateio,
            valorRateio: valorRateio 
          };
        });
      }
    }
    setAgrupado(agrupamentoInicial);
  }, [dadosLancamentos]);

  // ========= FILTRO, ORDENAÇÃO E SUB-AGRUPAMENTO POR CATEGORIA =========
  const filtradoEOrdenado = useMemo(() => {
    let resultado = agrupado
      .map(p => {
        const itensFiltrados = p.itens.filter(i => {
          const fData = (!filters.mes || i.mes === Number(filters.mes)) && 
                        (!filters.ano || i.ano === Number(filters.ano));
          const f1 = filters.projeto.length === 0 || filters.projeto.includes(p.projeto);
          const f2 = filters.categoria.length === 0 || filters.categoria.includes(i.categoria);
          const f3 = filters.empresa.length === 0 || filters.empresa.includes(i.empresa);
          const f4 = filters.conta.length === 0 || filters.conta.includes(i.conta);
          return fData && f1 && f2 && f3 && f4;
        });

        // Agrupando os itens filtrados por Categoria
        const categoriasMap = itensFiltrados.reduce((acc, item) => {
          if (!acc[item.categoria]) {
            acc[item.categoria] = { nome: item.categoria, total: 0, itens: [] };
          }
          acc[item.categoria].total += item.valor;
          acc[item.categoria].itens.push(item);
          return acc;
        }, {});

        return {
          ...p,
          itens: itensFiltrados,
          subCategorias: Object.values(categoriasMap),
          total: itensFiltrados.reduce((s, i) => s + i.valor, 0),
          valorRateioFiltrado: itensFiltrados.filter(i => i.isRateio).reduce((s, i) => s + i.valor, 0)
        };
      })
      .filter(p => p.itens.length > 0);

    if (ordenacao.campo) {
      resultado.sort((a, b) => {
        let vA, vB;
        switch (ordenacao.campo) {
          case 'projeto': vA = a.projeto.toLowerCase(); vB = b.projeto.toLowerCase(); break;
          case 'registro': vA = a.itens.length; vB = b.itens.length; break;
          case 'total': vA = a.total; vB = b.total; break;
          default: return 0;
        }
        return ordenacao.direcao === 'asc' ? (vA > vB ? 1 : -1) : (vA < vB ? 1 : -1);
      });
    }
    return resultado;
  }, [agrupado, filters, ordenacao]);

  // ========= CÁLCULOS DOS CARDS (USANDO SUA LÓGICA ORIGINAL) =========
  const totalProjetos = filtradoEOrdenado.length;
  const totalItens = filtradoEOrdenado.reduce((s, p) => s + p.itens.length, 0);
  const totalValorAgrupado = filtradoEOrdenado.reduce((s, p) => s + p.total, 0);
  const valorRateioTotal = filtradoEOrdenado.reduce((s, p) => s + (p.valorRateioFiltrado || 0), 0) * -1;
  const totalFinalExibicao = (totalValorAgrupado * -1) - valorRateioTotal;

  const toggleExpand = (projeto) => setExpandidos(prev => ({ ...prev, [projeto]: !prev[projeto] }));
  const limparFiltros = () => setFilters({ projeto: [], categoria: [], empresa: [], conta: [], mes: '', ano: '' });
  const formatCurrency = (v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // OPTIONS
  const optionsMeses = [{v:1,l:'Jan'},{v:2,l:'Fev'},{v:3,l:'Mar'},{v:4,l:'Abr'},{v:5,l:'Mai'},{v:6,l:'Jun'},{v:7,l:'Jul'},{v:8,l:'Ago'},{v:9,l:'Set'},{v:10,l:'Out'},{v:11,l:'Nov'},{v:12,l:'Dez'}];
  const optionsAnos = [...new Set(dadosLancamentos.map(i => i.ano))].filter(Boolean).sort().map(a => ({ value: a, label: String(a) }));

    const handleOrdenar = (campo) => {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc'
    }));
  };

  const optionsProjeto = [...new Set(agrupado.map(p => p.projeto))].map(p => ({ value: p, label: p }));
  const optionsCategoria = [...new Set(dadosLancamentos.map(i => i.categoria))].map(v => ({ value: v, label: v }));
  const optionsEmpresa = [...new Set(dadosLancamentos.map(i => i.empresa))].map(v => ({ value: v, label: v }));
  const optionsConta = [...new Set(dadosLancamentos.map(i => i.conta))].map(v => ({ value: v, label: v }));

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        background: "white",
        fontFamily: "'DM Sans', sans-serif",
        color: "grey",
      }}
    >
        <SideBarNavOperacao />

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >      
      <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <span className="text-2xl font-bold">YOUX</span>
          <span className="text-xl font-light">GROUP</span>
        </div>
        <button onClick={() => navigate('/inicio')} className="px-4 py-2 hover:bg-white/10 rounded-lg text-sm font-bold">Menu Principal</button>
      </header>

      <main className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Detalhamento de Custo</h1>

        {/* CARDS RESUMO */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-white p-3 rounded shadow">
            <div className="text-xs text-gray-600">Projetos</div>
            <div className="text-2xl font-bold text-purple-700">{totalProjetos}</div>
          </div>
          <div className="bg-white p-3 rounded shadow">
            <div className="text-xs text-gray-600">Registros</div>
            <div className="text-2xl font-bold text-purple-700">{totalItens}</div>
          </div>
          <div className="bg-white p-3 rounded shadow">
            <div className="text-xs text-gray-600">Valor Rateado</div>
            <div className="text-2xl font-bold text-blue-600">{formatCurrency(valorRateioTotal)}</div>
          </div>
          <div className="bg-white p-3 rounded shadow">
            <div className="text-xs text-gray-600">Total</div>
            <div className="text-2xl font-bold text-pink-700">{formatCurrency(totalFinalExibicao)}</div>
          </div>
        </div>

        {/* FILTROS COM DATA */}
        <div className="bg-white p-4 rounded shadow mb-6">
          <div className="grid grid-cols-6 gap-3 mb-3">
              <MultiSelect options={optionsProjeto} placeholder="Projetos" value={filters.projeto} onChange={(v) => setFilters(f => ({ ...f, projeto: v }))} />            
              <MultiSelect options={optionsCategoria} placeholder="Categorias" value={filters.categoria} onChange={(v) => setFilters(f => ({ ...f, categoria: v }))} />            
              <MultiSelect options={optionsEmpresa} placeholder="Empresas" value={filters.empresa} onChange={(v) => setFilters(f => ({ ...f, empresa: v }))} />           
              <MultiSelect options={optionsConta} placeholder="Contas" value={filters.conta} onChange={(v) => setFilters(f => ({ ...f, conta: v }))} />
              
              <select className="border rounded p-2 text-sm" value={filters.mes} onChange={(e) => setFilters(f => ({ ...f, mes: e.target.value }))}>
                <option value="">Mês</option>
                {optionsMeses.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
              </select>

              <select className="border rounded p-2 text-sm" value={filters.ano} onChange={(e) => setFilters(f => ({ ...f, ano: e.target.value }))}>
                <option value="">Ano</option>
                {optionsAnos.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
          </div>
          <button onClick={limparFiltros} className="text-sm text-pink-600 font-medium">Limpar Filtros</button>
        </div>

        {/* TABELA PRINCIPAL */}
        <div className="bg-white p-4 rounded shadow">
          {loading ? <div className="text-center py-8 text-gray-500">Carregando...</div> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2">Projeto <ArrowUpDown size={12} className="inline opacity-40 cursor-pointer" onClick={() => handleOrdenar('projeto')} /></th>
                  <th className="text-center py-3 px-2">Registros</th>
                  <th className="text-right py-3 px-2">Custo Direto</th>
                  <th className="text-right py-3 px-2">Rateio</th>
                  <th className="text-right py-3 px-2">Total <ArrowUpDown size={12} className="inline opacity-40 cursor-pointer" onClick={() => handleOrdenar('total')} /></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtradoEOrdenado.map(p => (
                  <React.Fragment key={p.projeto}>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="py-2 px-2 font-medium">{p.projeto}</td>
                      <td className="py-2 text-center">{p.itens.length}</td>
                      <td className="py-2 text-right">{formatCurrency((p.total - (p.valorRateioFiltrado || 0)) * -1)}</td>
                      <td className="py-2 text-right text-blue-600">{p.valorRateioFiltrado ? formatCurrency(p.valorRateioFiltrado * -1) : '-'}</td>
                      <td className="py-2 text-right font-bold">{formatCurrency(p.total * -1)}</td>
                      <td className="py-2 text-center">
                        <button onClick={() => toggleExpand(p.projeto)} className="text-indigo-600">{expandidos[p.projeto] ? '▼' : '▶'}</button>
                      </td>
                    </tr>
                    
                    {/* SUB-DETALHAMENTO POR CATEGORIA */}
                    {expandidos[p.projeto] && (
                      <tr>
                        <td colSpan={6} className="bg-gray-50 p-4">
                          <div className="space-y-2">
                            {p.subCategorias.map((sub, idx) => (
                              <details key={idx} className="bg-white border rounded shadow-sm">
                                <summary className="p-2 cursor-pointer hover:bg-gray-100 flex justify-between font-medium text-xs">
                                  <span>{sub.nome} ({sub.itens.length} lançamentos)</span>
                                  <span className="text-pink-700">{formatCurrency(sub.total * -1)}</span>
                                </summary>
                                <div className="p-2 overflow-x-auto">
                                  <table className="w-full text-[10px]">
                                    <thead>
                                      <tr className="border-b text-gray-500">
                                        <th className="text-left py-1">Empresa</th>
                                        <th className="text-left py-1">Conta</th>
                                        <th className="text-left py-1">Data</th>
                                        <th className="text-right py-1">Valor</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {sub.itens.map((i, idxI) => (
                                        <tr key={idxI} className={`border-t ${i.isRateio ? 'bg-blue-50' : ''}`}>
                                          <td className="py-1">{i.empresa}</td>
                                          <td className="py-1">{i.conta}</td>
                                          <td className="py-1">{i.data}</td>
                                          <td className={`text-right py-1 font-medium ${i.isRateio ? 'text-blue-600' : ''}`}>
                                            {formatCurrency(i.valor * -1)}
                                            {i.isRateio && <span className="ml-1 text-[8px]">(RATEIO)</span>}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </details>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
        </div>

  );
};

export default CustosDetalhados;